// Officer display-name derivation — web mirror of apps/mobile/src/officer-name.ts (parity
// contract, copy convention like live-config). A guard sees a human NAME — never the raw login
// email or a UUID; those belong on a Profile surface someday. Pure: no I/O.
//
// Order of preference:
//   1. session.user.user_metadata.full_name (trimmed, non-empty string)
//   2. title-cased email LOCAL-PART (the bit before "@", "+tag" dropped, separators → spaces)
//   3. the safe generic fallback ("Officer")
// Invariants: always non-empty, never contains "@", never echoes a UUID.

const FALLBACK_NAME = "Officer";

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** The minimal session shape this helper reads. Structural so plain test objects fit. */
export interface OfficerNameSession {
  readonly user?: {
    readonly email?: string | null;
    readonly id?: string | null;
    readonly user_metadata?: { readonly full_name?: unknown } | null;
  } | null;
}

/** Derive the officer's display name. Never returns a raw email or a UUID. */
export function deriveOfficerName(session: OfficerNameSession | null | undefined): string {
  const user = session?.user;

  const fullName = readFullName(user?.user_metadata);
  if (fullName !== null) {
    return fullName;
  }

  const fromEmail = nameFromEmail(user?.email);
  if (fromEmail !== null) {
    return fromEmail;
  }

  return FALLBACK_NAME;
}

function readFullName(metadata: { readonly full_name?: unknown } | null | undefined): string | null {
  const raw = metadata?.full_name;
  if (typeof raw !== "string") {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 && !UUID_PATTERN.test(trimmed) ? trimmed : null;
}

function nameFromEmail(email: string | null | undefined): string | null {
  if (typeof email !== "string") {
    return null;
  }
  const at = email.indexOf("@");
  if (at <= 0) {
    return null;
  }
  // Local part, with any "+tag" routing suffix dropped.
  let local = email.slice(0, at);
  const plus = local.indexOf("+");
  if (plus > 0) {
    local = local.slice(0, plus);
  }
  if (UUID_PATTERN.test(local)) {
    return null;
  }
  const words = local
    .split(/[._-]+/)
    .map((word) => word.trim())
    .filter((word) => word.length > 0)
    .map(titleCaseWord);
  if (words.length === 0) {
    return null;
  }
  return words.join(" ");
}

function titleCaseWord(word: string): string {
  return `${word.slice(0, 1).toUpperCase()}${word.slice(1).toLowerCase()}`;
}
